// eslint-disable jsx-a11y/heading-has-content
import { useForm } from '@tanstack/react-form';
import {
  getFragmentData,
  makeFragmentData,
  ProjectState,
  projectStateColourMap,
  projectStateDisplayMap,
  QueryMessageRole,
  QueryMessageStatus,
  ThemeType,
} from '@sc/graphql';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IconButton } from '../components/IconButton';
import { IconButtonInverted } from '../components/IconButtonInverted';
import {
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  ArrowUpLeftIcon,
  BookmarkIcon,
  ArrowLeftStartOnRectangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import { Pill } from '../components/Pill';
// Need the react prefix because of Vite types
import ClockRewind from '../icons/ClockRewind.svg?react';
import Edit from '../icons/Edit.svg?react';
import ChatBubble from '../icons/ChatBubble.svg?react';
import { ProcessingTip } from './ProcessingTip';
import { FullLoader, Loader } from '../components/Loader';
import { useFragment, useMutation, useQuery } from '@apollo/client/react';
import {
  GQL_FRAGMENT_PROJECT_QUERY,
  GQL_FRAGMENT_QUERY_MESSAGE,
  GQL_MUTATION_CREATE_QUERY,
  GQL_MUTATION_CREATE_QUERY_MESSAGE,
  GQL_MUTATION_DELETE_FILE,
  GQL_MUTATION_UPDATE_QUERY,
  GQL_QUERY_INDEX_QUERY_MESSAGES,
  GQL_QUERY_READ_QUERY,
} from '../../queries';
import classNames from 'classnames';
import { ChatMessage, PartialChatMessage } from '../components/ChatMessage';
import { CatchBoundary } from '@tanstack/react-router';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Tooltip } from 'react-tooltip';
import { Button, getButtonStyles } from '../components/Button';
import {
  FileToUploadInputType,
  // UploadButton, // file upload temporarily hidden
  UploadedFile,
} from '../components/UploadButton';
import { FileCard } from '../components/FileCard';
// file upload temporarily hidden
// import { supportedAIProcessingMimeTypes } from '../util/supportedMimeTypes';
import { Drawer } from '../components/Drawer';
import GlobeIcon from '../icons/globe-01.svg?react';
import { ChatHistory } from '../components/ChatHistory';
import { LinearLoader } from '../components/LinearLoader';
import { AccentButton } from '../components/AccentButton';
import { CircularProgress } from '../components/CircularProgress';
import { ChatTheme, ThemeProvider } from '../providers/ThemeContext';
import { useChatUsage } from '../hooks/useChatUsage';

// Mirrors the billing "Usage" tile bars (apps/frontend CreditTile) so the rate
// limits popover reads the same way. Bar fills as usage climbs, amber near full.
const UsageBar = ({
  label,
  value,
  subLeft,
  subRight,
  percent,
}: {
  label: string;
  value: string;
  subLeft?: string | undefined;
  subRight?: string | undefined;
  percent: number;
}): ReactElement => {
  const clamped = Math.max(0, Math.min(100, percent));

  return (
    <div className="flex flex-col space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-body-medium font-semibold">{label}</span>
        <span className="text-body-medium font-semibold">{value}</span>
      </div>
      <div className="relative h-1 w-full overflow-hidden rounded-xs bg-grey-t-800">
        <div
          className={classNames(
            'h-full rounded-xs transition-all duration-300 ease-out',
            {
              'bg-red-400': clamped >= 90,
              'bg-yellow-400': clamped >= 70 && clamped < 90,
              'bg-primary-400': clamped < 70,
            }
          )}
          style={{ width: `${clamped}%` }}
        />
      </div>
      {(subLeft ?? subRight) && (
        <div className="flex items-center justify-between text-xs text-text-low-priority">
          <span>{subLeft}</span>
          <span>{subRight}</span>
        </div>
      )}
    </div>
  );
};

interface Props {
  state?: ProjectState | undefined;
  sourceCount?: number | undefined;
  progress?: number | undefined;
  queryUuid?: string | undefined;
  projectUuid: string;
  navigateToQuery: (queryUuid: string) => void;
  onNewChat: () => void;
  onError: (error: unknown) => void;
  messageRetrievalEndpoint: string;
  onShowHistory: (show: boolean) => void;
  showHistory?: boolean | undefined;
  domains?: string[] | undefined;
  domainPrefix?: string | undefined;
  isSession?: boolean | undefined;
  // Anonymous public session (vs an authenticated magic-link visitor or owner).
  // Gates persistence-backed UI: history, bookmarks, titles.
  isPublic?: boolean | undefined;
  chatsUrl?: string | undefined;
  onLogout?: (() => void) | undefined;
  theme?: ChatTheme | null | undefined;
  title?: string | null | undefined;
  subtitle?: string | null | undefined;
  questions?: string[] | undefined;
  onGenerateQuestions?: (() => Promise<unknown>) | undefined;
  isGeneratingQuestions?: boolean | undefined;
  onEditQuestions?: (() => void) | undefined;
  onShowRateLimits?: (() => void) | undefined;
  onMessageSent?:
  | ((args: {
      isFirstMessage: boolean;
      queryUuid: string;
      // Set when a suggested-question chip initiated the query.
      question?: string | undefined;
    }) => void)
  | undefined;
}

export const Chat = ({
  state,
  // sessionId, // file upload temporarily hidden
  sourceCount = 0,
  progress = 100,
  navigateToQuery,
  queryUuid,
  projectUuid,
  onError,
  onNewChat,
  messageRetrievalEndpoint,
  onShowHistory,
  showHistory,
  domains = [],
  domainPrefix,
  isSession = false,
  isPublic = false,
  chatsUrl,
  onLogout,
  theme,
  title,
  subtitle,
  questions,
  onGenerateQuestions,
  isGeneratingQuestions = false,
  onEditQuestions,
  onShowRateLimits,
  onMessageSent,
}: Props): ReactElement => {
  const { error } = useQuery(GQL_QUERY_READ_QUERY, {
    variables: {
      uuid: queryUuid as string,
    },
    skip: !queryUuid,
  });

  const query = useFragment({
    fragment: GQL_FRAGMENT_PROJECT_QUERY,
    from: {
      __typename: 'ProjectQuery',
      uuid: queryUuid,
    },
  });

  useEffect(() => {
    if (error && queryUuid) {
      void onNewChat();
    }
  }, [error, onNewChat, query, queryUuid]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';

      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  const isQueryable = isSession || (progress === 100 && sourceCount > 0);

  // setUploading is consumed by the (temporarily hidden) file upload button
  const [uploading /*, setUploading */] = useState<FileToUploadInputType[]>([]);

  const [createQuery, { loading }] = useMutation(GQL_MUTATION_CREATE_QUERY, {
    onError,
  });

  const { data, loading: indexQueryMessagesLoading } = useQuery(
    GQL_QUERY_INDEX_QUERY_MESSAGES,
    {
      variables: {
        input: {
          filter: {
            queryUuid: queryUuid as string,
          },
          lastEvaluatedKey: null,
        },
      },
      skip: !queryUuid,
      context: {
        isBatched: true,
      },
    }
  );

  const [createQueryMessage, { loading: createQueryMessageLoading }] =
    useMutation(GQL_MUTATION_CREATE_QUERY_MESSAGE, {
      onError,
    });

  const [updateQuery] = useMutation(GQL_MUTATION_UPDATE_QUERY, {
    update: (cache) => {
      cache.evict({
        fieldName: 'indexQueries',
      });

      cache.gc();
    },
  });

  const [deleteFile] = useMutation(GQL_MUTATION_DELETE_FILE, {
    onError,
  });

  const chipQuestionRef = useRef<string | null>(null);

  const form = useForm({
    defaultValues: {
      content: '',
      files: [] as UploadedFile[],
    },
    onSubmit: ({ value, formApi }) => {
      const chipQuestion =
        chipQuestionRef.current === value.content
          ? chipQuestionRef.current
          : undefined;

      chipQuestionRef.current = null;

      if (!queryUuid) {
        void createQuery({
          variables: {
            input: {
              projectUuid,
              content: value.content,
              fileUuids: value.files.map((f) => f.uuid),
              question: chipQuestion,
            },
          },
          update: (cache, { data }) => {
            if (!data?.createQuery) return;

            cache.writeQuery({
              query: GQL_QUERY_INDEX_QUERY_MESSAGES,
              variables: {
                input: {
                  filter: {
                    queryUuid: data.createQuery.query.uuid,
                  },
                  lastEvaluatedKey: null,
                },
              },
              data: {
                indexQueryMessages: {
                  __typename: 'QueryMessagePage',
                  items: [data.createQuery.user, data.createQuery.model],
                  lastEvaluatedKey: null,
                },
              },
            });
          },
          onCompleted: (data) => {
            if (!isPublic) {
              void updateQuery({
                variables: {
                  input: {
                    uuid: data.createQuery.query.uuid,
                    generateTitle: value.content,
                    isBookmarked: undefined,
                  },
                },
              });
            }

            onMessageSent?.({
              isFirstMessage: true,
              queryUuid: data.createQuery.query.uuid,
              question: chipQuestion,
            });

            navigateToQuery(data.createQuery.query.uuid);

            formApi.reset();
          },
        });
      } else {
        void createQueryMessage({
          variables: {
            input: {
              queryUuid,
              content: value.content,
              fileUuids: value.files.map((f) => f.uuid),
            },
          },
          update: (cache, data) =>
            cache.updateQuery(
              {
                query: GQL_QUERY_INDEX_QUERY_MESSAGES,
                variables: {
                  input: {
                    filter: {
                      queryUuid,
                    },
                    lastEvaluatedKey: null,
                  },
                },
              },
              (d) =>
                d && data.data?.createQueryMessage
                  ? {
                    ...d,
                    indexQueryMessages: {
                      ...d.indexQueryMessages,
                      items: [
                        ...d.indexQueryMessages.items,
                        data.data.createQueryMessage.user,
                        data.data.createQueryMessage.model,
                      ],
                    },
                  }
                  : null
            ),
          onCompleted: () => {
            onMessageSent?.({ isFirstMessage: false, queryUuid });

            formApi.reset();
            if (textareaRef.current) {
              textareaRef.current.style.height = 'auto';
            }
          },
        });
      }
    },
  });

  const ref = useRef<HTMLDivElement>(null);

  const isUserScrolling = useRef(false);

  useEffect(() => {
    if (data && ref.current && !isUserScrolling.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [data]);

  const queryMessages = data?.indexQueryMessages.items.map((i) =>
    getFragmentData(GQL_FRAGMENT_QUERY_MESSAGE, i)
  );

  const customDomain = domains[0];

  const [isDispatchingQuestions, setIsDispatchingQuestions] = useState(false);

  // Shuffled once per mount; a rotating window of 3 advances every 15s.
  const shuffledQuestions = useMemo(
    () =>
      (questions ?? [])
        .map((question) => ({ question, order: Math.random() }))
        .sort((a, b) => a.order - b.order)
        .map(({ question }) => question),
    [questions]
  );

  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>(() =>
    shuffledQuestions.slice(0, 3)
  );

  // Chip currently fading out ahead of its swap, if any.
  const [fadingSlotIndex, setFadingSlotIndex] = useState<number | null>(null);

  const nextQuestionIndexRef = useRef(3);

  useEffect(() => {
    setSuggestedQuestions(shuffledQuestions.slice(0, 3));

    nextQuestionIndexRef.current = 3;

    setFadingSlotIndex(null);
  }, [shuffledQuestions]);

  useEffect(() => {
    if (shuffledQuestions.length <= 3 || queryUuid) return;

    let fadeTimeout: ReturnType<typeof setTimeout> | undefined;

    // Every 5s pick one chip, fade it out, swap it for a question not
    // currently on screen, fade back in.
    const interval = setInterval(() => {
      const slot = Math.floor(Math.random() * 3);

      setFadingSlotIndex(slot);

      fadeTimeout = setTimeout(() => {
        setSuggestedQuestions((prev) => {
          const next = [...prev];

          let guard = 0;

          let candidate =
            shuffledQuestions[
              nextQuestionIndexRef.current % shuffledQuestions.length
            ];

          while (
            candidate !== undefined &&
            next.includes(candidate) &&
            guard < shuffledQuestions.length
          ) {
            nextQuestionIndexRef.current++;

            guard++;

            candidate =
              shuffledQuestions[
                nextQuestionIndexRef.current % shuffledQuestions.length
              ];
          }

          nextQuestionIndexRef.current++;

          if (candidate !== undefined) next[slot] = candidate;

          return next;
        });

        setFadingSlotIndex(null);
      }, 300);
    }, 5_000);

    return () => {
      clearInterval(interval);

      if (fadeTimeout) clearTimeout(fadeTimeout);

      setFadingSlotIndex(null);
    };
  }, [shuffledQuestions, queryUuid]);

  const isInverted = theme?.themeType === ThemeType.light;

  const ChatIconButton = isInverted ? IconButtonInverted : IconButton;

  const {
    rateLimit: sessionRateLimit,
    showUsageMarker,
    usageFraction,
    usagePercent,
    usageMarkerColour,
    resetMinutes,
  } = useChatUsage({
    query: query.complete ? query.data : undefined,
    brandColour: theme?.brandColour,
  });

  const usageTooltip = `Based on your rate limits, this chat has used ${usagePercent}% of context.`;

  const usageMarkerButton = getButtonStyles({ type: 'simple', size: 'small' });

  const usageMarkerClassName = classNames(
    usageMarkerButton.className,
    isInverted && 'text-black-t-400!'
  );

  const newChatButton = getButtonStyles({ type: 'primary', size: 'small' });

  const chatUsageSpent = usagePercent >= 100;

  const usageMarkerContent = (
    <CircularProgress
      size={18}
      strokeWidth={2}
      fraction={usageFraction}
      colour={usageMarkerColour}
      animate
    />
  );

  return (
    <ThemeProvider theme={theme}>
      <div
        className={classNames(
          'flex flex-col grow overflow-hidden h-full',
          isInverted && 'bg-white text-black'
        )}
      >
        <div className="p-5 flex items-center justify-between w-full relative">
          <div className="space-x-2">
            {!isSession && state === ProjectState.published && domainPrefix && (
              <ChatIconButton
                Icon={ArrowTopRightOnSquareIcon}
                tooltip="Open"
                onClick={() =>
                  window.open(
                    `${chatsUrl ?? 'https://chat.starkchat.com'
                    }/chat/${domainPrefix}`,
                    '_blank',
                    'noreferrer noopener'
                  )
                }
              />
            )}
            {onLogout && (
              <ChatIconButton
                Icon={ArrowLeftStartOnRectangleIcon}
                tooltip="Logout"
                onClick={onLogout}
              />
            )}
          </div>
          {state && !isSession && (
            <div className="absolute flex items-center space-x-2 left-1/2 -translate-x-1/2">
              {customDomain && (
                <>
                  <GlobeIcon className="size-4 text-grey-400" />
                  <span className="text-sm">
                    {customDomain}
                    {domains.length > 1 && ` + ${domains.length - 1}`}
                  </span>
                </>
              )}
              <Pill
                text={projectStateDisplayMap[state]}
                colour={projectStateColourMap[state]}
                size="small"
              />
            </div>
          )}
          <div className="flex items-center space-x-2">
            {showUsageMarker &&
              (onShowRateLimits ? (
                <button
                  type="button"
                  onClick={onShowRateLimits}
                  className={usageMarkerClassName}
                  style={usageMarkerButton.style}
                  data-tooltip-id="query-usage"
                  data-tooltip-content={usageTooltip}
                  data-tooltip-place="bottom"
                  data-tooltip-delay-show={350}
                >
                  {usageMarkerContent}
                  <Tooltip
                    openEvents={{ mouseover: true }}
                    closeEvents={{ click: true, mouseleave: true, blur: true }}
                    className="z-110"
                    style={{ maxWidth: '16rem', whiteSpace: 'normal' }}
                    id="query-usage"
                  />
                </button>
              ) : (
                <Popover className="relative">
                  <PopoverButton
                    className={usageMarkerClassName}
                    style={usageMarkerButton.style}
                  >
                    {usageMarkerContent}
                  </PopoverButton>
                  <PopoverPanel
                    anchor={{ to: 'bottom', gap: 8, padding: 20 }}
                    transition
                    className="z-110 flex w-[calc(100vw-2.5rem)] max-w-105! origin-top flex-col rounded border border-grey-t-800 bg-background-tertiary text-white shadow-xl transition data-closed:-translate-y-full data-closed:opacity-0"
                  >
                    {({ close }) => (
                      <div>
                        <div className="flex items-center justify-between border-b border-b-grey-700 p-4">
                          <h2 className="font-grotesque text-h2 font-bold">
                            Usage
                          </h2>
                          <IconButton
                            onClick={() => close()}
                            size="large"
                            Icon={XMarkIcon}
                            tooltip="Close"
                          />
                        </div>
                        <div className="my-5 flex w-full flex-col space-y-5 px-5">
                          {sessionRateLimit ? (
                            <>
                              <UsageBar
                                label="This chat"
                                value={`${usagePercent}% used`}
                                percent={usagePercent}
                              />
                              <UsageBar
                                label="Questions this hour"
                                value={`${sessionRateLimit.queriesUsedThisHour} / ${sessionRateLimit.queriesPerUserPerHour}`}
                                subRight={
                                  resetMinutes !== null &&
                                    sessionRateLimit.queriesUsedThisHour > 0
                                    ? `resets in ${resetMinutes} min`
                                    : undefined
                                }
                                percent={
                                  (sessionRateLimit.queriesUsedThisHour /
                                    sessionRateLimit.queriesPerUserPerHour) *
                                  100
                                }
                              />
                            </>
                          ) : (
                            <p className="text-text-low-priority">
                              No usage yet
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </PopoverPanel>
                </Popover>
              ))}
            {!isPublic && queryUuid && query.complete && (
              <ChatIconButton
                onClick={() =>
                  void updateQuery({
                    variables: {
                      input: {
                        uuid: query.data.uuid,
                        isBookmarked: !query.data.isBookmarked,
                        generateTitle: undefined,
                      },
                    },
                    update: (cache) => {
                      cache.evict({
                        fieldName: 'indexQueries',
                      });

                      cache.gc();
                    },
                    optimisticResponse: {
                      updateQuery: makeFragmentData(
                        {
                          ...query.data,
                          isBookmarked: !query.data.isBookmarked,
                        },
                        GQL_FRAGMENT_PROJECT_QUERY
                      ),
                    },
                  })
                }
                Icon={
                  query.data.isBookmarked ? BookmarkIconSolid : BookmarkIcon
                }
                tooltip="Bookmark chat"
              />
            )}
            {isQueryable && (
              <>
                {!isPublic && (
                  <ChatIconButton
                    onClick={() => onShowHistory(true)}
                    Icon={ClockRewind}
                    active={showHistory}
                    tooltip="Chat history"
                  />
                )}
                {isInverted ? (
                  <IconButtonInverted
                    onClick={onNewChat}
                    type="primary"
                    Icon={Edit}
                    accentColour={theme.brandColour}
                  />
                ) : (
                  <IconButton tooltip='New chat' onClick={onNewChat} type="subtle" Icon={Edit} />
                )}
              </>
            )}
          </div>
        </div>
        <div
          className="overflow-y-auto overflow-x-hidden overscroll-contain flex-1"
          ref={ref}
          onScroll={(e) => {
            const el = e.currentTarget;

            isUserScrolling.current =
              el.scrollTop + el.clientHeight < el.scrollHeight - 50;
          }}
        >
          <div className="relative w-full min-h-full flex flex-col">
            {!isSession && (
              <>
                {sourceCount === 0 && (
                  <div className="mt-16 md:mt-40 flex flex-col items-center mx-auto text-center max-w-87.5">
                    <div className="flex items-center justify-center p-6 border rounded-full border-secondary-300 size-fit mb-4">
                      <ChatBubble className="text-secondary-300" />
                    </div>
                    <h1 className="text-h1 font-semibold mb-2 font-grotesque">
                      No sources configured
                    </h1>
                    <p className="text-text-subtle">
                      Please add at least one source to preview your AI
                      knowledge base
                    </p>
                  </div>
                )}

                {progress < 100 && sourceCount > 0 && (
                  <div className="my-auto flex flex-col items-center mx-auto text-center max-w-87.5">
                    <div className="flex items-center justify-center p-6 rounded-sm size-fit mb-4 bg-primary-400/10">
                      <Loader className="size-9" fill="#55C78E" />
                    </div>
                    <h1 className="text-h1 font-semibold mb-2 font-grotesque">
                      Processing {progress}%
                    </h1>
                    <p className="text-text-low-priority mb-5">
                      We're working hard in the background to proccess your
                      chat, in the meantime, check out the tip below:
                    </p>
                    {!!progress && (
                      <LinearLoader variant="determinate" value={progress} />
                    )}
                    <ProcessingTip />
                  </div>
                )}
              </>
            )}
            {isQueryable && (
              <>
                {!queryUuid ? (
                  <div className="mt-16 md:mt-40 mx-auto text-center flex flex-col items-center gap-2 px-5">
                    {theme?.logoSrc && (
                      <div
                        className={classNames(
                          'p-2.5 mb-2 w-20 flex items-center justify-center',
                          isInverted && 'bg-white'
                        )}
                      >
                        <img
                          src={theme.logoSrc}
                          alt=""
                          className="w-full h-auto"
                        />
                      </div>
                    )}
                    <h1 className="text-h1 font-semibold font-grotesque">
                      {title ?? theme?.title ?? 'Ask me anything'}
                    </h1>
                    <p className={theme ? undefined : 'text-text-subtle'}>
                      {subtitle ??
                        theme?.subtitle ??
                        (isSession
                          ? 'Your knowledge base is ready to answer!'
                          : 'Your new knowledge base is ready to be tested')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6 flex flex-col h-full grow mt-5 px-5 md:px-20 w-full mb-10">
                    {queryMessages?.map((i, idx, arr) => (
                      <div
                        key={i.uuid}
                        className="max-w-180 mx-auto w-full flex flex-col group"
                      >
                        <CatchBoundary
                          getResetKey={() => i.uuid}
                          errorComponent={(err) => (
                            <div className="text-red-400">
                              <span className="text-sm">
                                Err: {err.error.message}
                              </span>
                            </div>
                          )}
                        >
                          {i.role === QueryMessageRole.user ? (
                            <div className="w-fit self-end max-w-[80%]">
                              <div className="overflow-hidden mb-2">
                                <div className="overflow-x-auto flex space-x-3 pr-px justify-end">
                                  {i.files.map((file) => (
                                    <FileCard
                                      classNames="!rounded-[40px] bg-background-secondary border-none"
                                      key={file.uuid}
                                      uuid={file.uuid}
                                      name={file.name}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="py-3 px-5 bg-background-tertiary border border-grey-t-700 rounded-[40px] text-white">
                                <span>
                                  {i.parts.map((p) => p.text).join('')}
                                </span>
                              </div>
                            </div>
                          ) : i.status === QueryMessageStatus.completed ? (
                            <ChatMessage
                              message={i}
                              idx={idx}
                              arrLength={arr.length}
                              queryUuid={queryUuid}
                              onError={onError}
                            />
                          ) : (
                            <PartialChatMessage
                              message={i}
                              idx={idx}
                              arrLength={arr.length}
                              messageRetrievalEndpoint={
                                messageRetrievalEndpoint
                              }
                              queryUuid={queryUuid}
                              onError={onError}
                            />
                          )}
                        </CatchBoundary>
                      </div>
                    )) ??
                      (!!indexQueryMessagesLoading && <FullLoader />)}
                  </div>
                )}

                <div
                  className={classNames('px-5 md:px-20 mt-5 transition-all', {
                    'sticky bottom-2.5 md:bottom-5 w-full z-100': queryUuid,
                  })}
                >
                  {chatUsageSpent ? (
                    <div
                      className={classNames(
                        'mx-auto flex w-full max-w-170 flex-col items-center space-y-4 rounded-xs border p-5 text-center',
                        isInverted
                          ? 'bg-white border-black-t-800 text-black-t-300 shadow-xs'
                          : 'bg-background-secondary border-grey-t-700'
                      )}
                    >
                      <p
                        className={classNames(
                          'text-sm',
                          !isInverted && 'text-text-low-priority'
                        )}
                      >
                        You've reached the limit for this chat. Start a new one
                        to keep asking questions.
                      </p>
                      <button
                        type="button"
                        onClick={onNewChat}
                        className={newChatButton.className}
                        style={newChatButton.style}
                      >
                        New chat
                      </button>
                    </div>
                  ) : (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();

                        e.stopPropagation();

                        void form.handleSubmit();
                      }}
                      className={classNames(
                        'w-full rounded-xs border relative p-3 mx-auto max-w-170',
                        isInverted
                          ? 'bg-white border-black-t-800 shadow-xs transition-colors focus-within:border-black-t-600'
                          : 'bg-background-secondary border-grey-t-700'
                      )}
                    >
                      <form.Field
                        name="files"
                        children={({ state, removeValue }) =>
                          (!!state.value.length || !!uploading.length) && (
                            <div className="overflow-hidden mb-5">
                              <div className="overflow-x-auto flex space-x-2 pr-px">
                                {state.value.map((file, idx) => (
                                  <FileCard
                                    key={file.uuid}
                                    uuid={file.uuid}
                                    name={file.name}
                                    onDelete={() => {
                                      removeValue(idx);

                                      void deleteFile({
                                        variables: {
                                          uuid: file.uuid,
                                        },
                                      });
                                    }}
                                  />
                                ))}
                                {uploading.map((file) => (
                                  <FileCard
                                    key={file.uuid}
                                    loading
                                    uuid={file.uuid}
                                    name={file.file.name}
                                  />
                                ))}
                              </div>
                            </div>
                          )
                        }
                      />
                      <form.Field
                        name="content"
                        children={({ handleChange, state }) => (
                          <textarea
                            ref={textareaRef}
                            rows={1}
                            className={classNames(
                              'resize-none w-full outline-none max-h-50 overflow-y-auto',
                              isInverted
                                ? 'text-black placeholder:text-black-t-400'
                                : 'placeholder-shown:text-text-subtle'
                            )}
                            placeholder="Type your questions here..."
                            required
                            value={state.value}
                            onChange={(e) => handleChange(e.target.value)}
                            onFocus={() => (isUserScrolling.current = false)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.stopPropagation();

                                e.preventDefault();
                                if (
                                  state.value.length > 0 &&
                                  !uploading.length
                                ) {
                                  void form.handleSubmit();
                                }
                              }
                            }}
                            onInput={adjustTextareaHeight}
                          />
                        )}
                      />
                      <div className="flex items-center justify-end mt-3">
                        <div className="flex items-center space-x-2">
                          {loading ||
                            createQueryMessageLoading ||
                            form.state.isSubmitting ? (
                            <div className="size-10 flex items-center justify-center">
                              <Loader
                                className="size-4.5"
                                fill={theme?.brandColour}
                              />
                            </div>
                          ) : (
                            <>
                              {/* file upload temporarily hidden — re-enable by uncommenting this block and the related imports/props tagged "file upload temporarily hidden"
                          <form.Field
                            name="files"
                            children={({ handleChange, state }) => (
                              <UploadButton
                                multiple
                                type="tertiary"
                                size="medium"
                                inverted={isInverted}
                                disabled={state.value.length >= 2}
                                fileLimit={2 - state.value.length}
                                uploading={uploading}
                                setUploading={setUploading}
                                keyPrefix={`projects/${projectUuid}/queries/${sessionId}/uploads`}
                                onFilesUpload={(f) =>
                                  handleChange([...f, ...state.value])
                                }
                                accept={[
                                  ...supportedAIProcessingMimeTypes.chunkableTextMimeTypes,
                                  ...supportedAIProcessingMimeTypes.anthropicImageMimeTypes,
                                  ...supportedAIProcessingMimeTypes.anthropicDocumentMimeTypes,
                                  ...Object.keys(
                                    supportedAIProcessingMimeTypes.castToTextMimeTypes
                                  ),
                                ].join(',')}
                                resolveMimeType={(mimeType) =>
                                  supportedAIProcessingMimeTypes
                                    .castToTextMimeTypes[mimeType] ?? mimeType
                                }
                                projectUuid={projectUuid}
                                setDeletedAt
                              />
                            )}
                          />
                          */}
                              {theme ? (
                                <AccentButton
                                  Icon={ArrowUpIcon}
                                  accentColour={theme.brandColour}
                                  disabled={!!uploading.length}
                                  isSubmit
                                />
                              ) : (
                                <IconButton
                                  Icon={ArrowUpIcon}
                                  type="primary"
                                  size="medium"
                                  disabled={!!uploading.length}
                                  isSubmit
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </form>
                  )}
                  <p
                    className={classNames(
                      'mx-auto w-full max-w-170 pt-4 text-center text-xs',
                      isInverted ? 'text-black-t-400' : 'text-text-low-priority'
                    )}
                  >
                    Stark Chat is AI and can make mistakes. Please double-check responses.
                  </p>
                  {!queryUuid &&
                    !chatUsageSpent &&
                    (suggestedQuestions.length > 0 ||
                      !!onGenerateQuestions ||
                      isGeneratingQuestions) && (
                      <div className="mx-auto mt-6 w-full max-w-170 animate-slide-in">
                        <div className="mb-3 flex items-center justify-between">
                          <p
                            className={classNames(
                              'text-sm font-semibold',
                              isInverted ? 'text-black' : 'text-white'
                            )}
                          >
                            Questions you could ask
                          </p>
                          {onEditQuestions && (
                            <button
                              type="button"
                              onClick={onEditQuestions}
                              className={classNames(
                                'cursor-pointer text-sm underline transition-opacity hover:opacity-75',
                                isInverted
                                  ? 'text-black-t-400'
                                  : 'text-text-low-priority'
                              )}
                            >
                              Edit questions
                            </button>
                          )}
                        </div>
                        {suggestedQuestions.length > 0 ? (
                          <div className="flex flex-col items-start gap-2">
                            {suggestedQuestions.map((question, index) => (
                              // Keyed by slot so the node survives the swap —
                              // a text-keyed node would remount and skip the
                              // fade-in.
                              <div
                                key={index}
                                // Staggered entrance; fill-mode keeps chips
                                // hidden until their delay elapses.
                                style={{
                                  animationDelay: `${index * 75}ms`,
                                  animationFillMode: 'both',
                                }}
                                className={classNames(
                                  'animate-slide-in transition-opacity duration-300 ease-in-out',
                                  fadingSlotIndex === index
                                    ? 'opacity-0'
                                    : 'opacity-100'
                                )}
                              >
                                <button
                                  type="button"
                                  disabled={loading || form.state.isSubmitting}
                                  onClick={() => {
                                    chipQuestionRef.current = question;

                                    form.setFieldValue('content', question);

                                    void form.handleSubmit();
                                  }}
                                  className={classNames(
                                    'flex cursor-pointer items-center gap-3 rounded-xs px-4 py-2.5 text-left text-sm',
                                    isInverted
                                      ? 'bg-black-t-900 text-black transition-colors hover:bg-black-t-800'
                                      : 'bg-background-secondary text-white transition-opacity hover:opacity-75'
                                  )}
                                >
                                  <span>{question}</span>
                                  <ArrowUpLeftIcon
                                    className={classNames(
                                      'size-3 shrink-0',
                                      isInverted
                                        ? 'text-black-t-400'
                                        : 'text-text-low-priority'
                                    )}
                                  />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : isGeneratingQuestions || isDispatchingQuestions ? (
                          <div className="flex items-center gap-2">
                            <Loader
                              className="size-4"
                              fill={theme?.brandColour}
                            />
                            <span
                              className={classNames(
                                'text-sm',
                                isInverted
                                  ? 'text-black-t-400'
                                  : 'text-text-low-priority'
                              )}
                            >
                              Generating questions...
                            </span>
                          </div>
                        ) : (
                          onGenerateQuestions && (
                            <Button
                              button={{
                                text: 'Generate example questions',
                                type: 'secondary',
                                size: 'small',
                                shrink: true,
                                onClick: () => {
                                  setIsDispatchingQuestions(true);

                                  void onGenerateQuestions().finally(() =>
                                    setIsDispatchingQuestions(false)
                                  );
                                },
                              }}
                            />
                          )
                        )}
                      </div>
                    )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Drawer
        hideBorder
        title="History"
        setOpen={(open) => onShowHistory(open)}
        open={!!showHistory}
      >
        <ChatHistory
          navigateToQuery={navigateToQuery}
          projectUuid={projectUuid}
        />
      </Drawer>
    </ThemeProvider>
  );
};
